import {
  actionColumn,
  Button,
  collapseColumn,
  column,
  Css,
  dateColumn,
  defaultPage,
  emptyCell,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  IconButton,
  multiFilter,
  PageHeaderLayout,
  simpleHeader,
  Tag,
  toggleFilter,
  Tooltip,
  treeFilter,
  useGridTableLayoutState,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { chipCell, dateCell } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  BidPackageBidderKind,
  BidPackageGroupOrderBy,
  BidPackageGroupsFilter,
  BidPackageStatus,
  BidScopeSource,
  InternalEstimateType,
  Maybe,
  Order,
  ReadyPlanStatus,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  BidPackageDetailFragment,
  BidPackageGroupDetailFragment,
  BidPackageGroupsQuery,
  BidPackagesPage_BidPackageBidderFragment,
  ProjectBidPackagesPageMetadataFragment,
  useBidPackageGroupsQuery,
  useBidPackagesPageMetadataQuery,
  useDeleteBidPackageMutation,
} from "src/routes/bids/BidPackagesPage.generated";
import {
  createBidPackagesDetailUrl,
  createBidPackagesEditUrl,
  createBidPackageUrl,
  createPlanBasedBidPackageEditUrl,
  createPlanBasedBidPackageUrl,
} from "src/RouteUrls";
import { groupBy, queryResult } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { useProjectContext } from "../projects/context/ProjectContext";

export function BidPackagesPage() {
  // Loading markets, projects, and bid categories before rendering the page
  const query = useBidPackagesPageMetadataQuery();
  return queryResult(query, {
    data: ({ costCodes, projects }) => <BidPackagesPageView projects={projects} costCodes={costCodes} />,
  });
}

export function ProjectBidPackagesPage() {
  const query = useBidPackagesPageMetadataQuery();
  return queryResult(query, { data: ({ costCodes }) => <BidPackagesPageView costCodes={costCodes} /> });
}

type BidPackagesPageViewProps = { costCodes: NamedFragment[]; projects?: ProjectBidPackagesPageMetadataFragment[] };

function BidPackagesPageView({ costCodes, projects }: BidPackagesPageViewProps) {
  const project = useProjectContext();
  const { projectId } = useRouteParams<{ projectId?: string }>();
  const { openModal } = useModal();
  const [deleteBidPackage] = useDeleteBidPackageMutation({ refetchQueries: ["BidPackageGroups"] });
  const [order, setOrder] = useState<BidPackageGroupOrderBy>({ updatedAt: Order.Desc });
  const filterDefs: FilterDefs<BidPackageGroupsFilter> = useMemo(
    () => ({
      ...(Array.isArray(projects)
        ? {
            project: treeFilter({
              defaultCollapsed: true,
              label: "Markets and Communities",
              filterBy: "leaf",
              getOptionLabel: (o) => o.name,
              getOptionValue: (o) => o.id,
              options: Object.values(
                groupBy(
                  projects.filter((p) => p.market),
                  (p) => p.market!.id,
                ),
              ).map((p) => ({
                id: p[0].market!.id,
                name: p[0].market!.name,
                children: p.map((d) => ({ id: d.id, name: d.name })),
              })),
            }),
          }
        : {}),
      costCode: multiFilter({
        label: "Cost Code",
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
        options: costCodes,
      }),
      // If we are viewing project bid packages, include the toggle to show bids from all levels
      ...(project.id
        ? { includeRelatedProjectBids: toggleFilter({ label: "Show Bids from All Levels", defaultValue: true }) }
        : {}),
    }),
    // TODO: validate this eslint-disable. It was automatically ignored when the oxlint migration restored `react-hooks/exhaustive-deps` to an error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, costCodes],
  );

  const layoutState = useGridTableLayoutState<BidPackageGroupsFilter>({
    persistedFilter: { storageKey: "bidPackageGroupsFilter", filterDefs },
    search: "server",
  });

  const { includeRelatedProjectBids, ...additionalFilters } = layoutState.filter;

  const query = useBidPackageGroupsQuery({
    variables: {
      filter: {
        search: layoutState.searchString || undefined,
        project: projectId ? [projectId] : undefined,
        // If we are viewing subdivision bid packages, include child project bids toggle determines if Lot bids are included
        ...(project.__typename === "Subdivision"
          ? {
              scopeSource: includeRelatedProjectBids
                ? [BidScopeSource.Subdivision, BidScopeSource.Lot]
                : [BidScopeSource.Subdivision],
            }
          : { includeRelatedProjectBids }),
        ...additionalFilters,
      },
      orderBy: order,
      packageOrderBy: order,
      page: defaultPage,
      costCode: layoutState.filter.costCode,
    },
  });

  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.bidPackageGroups.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.bidPackageGroups.entities.length, limit: defaultPage.limit } },
      });
    }
  }, [query]);

  const onDelete = useCallback(
    (bidPackage: BidPackageDetailFragment) => {
      openModal({
        content: (
          <ConfirmationModal
            confirmationMessage={
              <>
                Are you sure you want to delete this bid package <b>{bidPackage.name}</b>? It will not be archived and
                cannot be undone.
              </>
            }
            onConfirmAction={async () => {
              await deleteBidPackage({ variables: { id: bidPackage.id } });
            }}
            title="Delete Bid Package"
            label="Yes, Delete"
          />
        ),
      });
    },
    [deleteBidPackage, openModal],
  );

  const columns = useMemo(() => createColumns(onDelete, projectId), [projectId, onDelete]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Bid Packages",
        actions: [
          {
            kind: "menu" as const,
            trigger: { label: "Create", variant: "primary" as const },
            items: [
              { label: "Create Plan Based Package", onClick: createPlanBasedBidPackageUrl({ projectId }) },
              { label: "Create Unit Based Package", onClick: createBidPackageUrl("add", { projectId }) },
            ],
          },
        ],
      }}
    >
      <GridTableLayout
        layoutState={layoutState}
        hideEditColumns
        tableProps={{
          query,
          createRows,
          columns,
          as: "virtual",
          sorting: {
            on: "server",
            onSort: (key, direction) => setOrder(toOrder(key, direction)),
            value: parseOrder(order),
          },
          emptyFallback: "There aren't any bid packages to display.",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
        }}
      />
    </PageHeaderLayout>
  );
}

type HeaderRow = { kind: "header" };
type BidPackageGroupedRow = { kind: "group"; data: BidPackageGroupDetailFragment; children: BidPackageRow[] };
type BidPackageRow = { kind: "package"; data: BidPackageDetailFragment };
type Row = HeaderRow | BidPackageGroupedRow | BidPackageRow;

const createColumns = (
  onDelete: (bidPackage: BidPackageDetailFragment) => void,
  projectId?: string,
): GridColumn<Row>[] => [
  collapseColumn<Row>(),
  column<Row>({
    header: "Name",
    w: 2,
    mw: "220px",
    group: ({ id, displayName, isPlanBasedV2 }) => (
      <Button
        variant="text"
        onClick={
          isPlanBasedV2 ? createPlanBasedBidPackageEditUrl(id, projectId) : createBidPackagesEditUrl(id, projectId)
        }
        label={<span css={Css.mdSb.$}>{displayName}</span>}
      />
    ),
    package: ({
      name,
      bidPackageGroup,
      id,
      latestVersion,
      status,
      availableUpdates,
      costCodesName,
      isPlanBasedV2,
      isMissingOptions,
    }) => ({
      content: () => {
        const firstDraft = latestVersion.version === 1 && status.code === BidPackageStatus.Draft;
        const href = firstDraft
          ? isPlanBasedV2
            ? createPlanBasedBidPackageEditUrl(bidPackageGroup!.id, projectId)
            : createBidPackagesEditUrl(bidPackageGroup!.id, projectId)
          : createBidPackagesDetailUrl(bidPackageGroup!.id, id);
        return (
          <div css={Css.df.fdr.aic.gap2.$}>
            <Button
              variant="text"
              label={
                <FormattedBidName
                  costCodesName={costCodesName}
                  latestVersion={latestVersion}
                  isPlanBasedV2={isPlanBasedV2}
                />
              }
              onClick={href}
            />
            {!isPlanBasedV2 && availableUpdates.nonEmpty && (
              <Tag text="Updates available" icon="arrowFromBottom" iconOnly type="update" />
            )}
            {isPlanBasedV2 && (
              <div css={Css.df.fdr.aic.gap1.w100.$}>
                {availableUpdates.nonEmpty && <Tag text="Review Scope Changes" icon="refresh" iconOnly type="info" />}
                {isMissingOptions && <Tag text="Add Missing Options" icon="error" iconOnly type="warning" />}
              </div>
            )}
          </div>
        );
      },
      value: name,
      css: Css.sm.$,
    }),
  }),
  column<Row>({
    header: "Bid Type",
    group: ({ bidPackages }) => {
      if (bidPackages.first?.isPlanBasedV2) return "Plan V2";
      if (bidPackages.first?.isUnitBased) return "Unit";
      return "Plan V1";
    },
    package: ({ isPlanBasedV2, isUnitBased }) => {
      if (isPlanBasedV2) return "Plan V2";
      if (isUnitBased) return "Unit";
      return "Plan V1";
    },
    w: "90px",
    mw: "90px",
  }),
  column<Row>({ header: "Cost Type", group: emptyCell, package: ({ costType }) => costType.name, mw: "100px" }),
  column<Row>({
    header: "Product Offerings",
    group: emptyCell,
    package: ({ latestVersion, projects }) => ({
      content: <ProductOfferingsBadge latestVersion={latestVersion} projects={projects} />,
    }),
    mw: "140px",
  }),
  column<Row>({
    header: "Hierarchy",
    group: ({ projects, bidPackages }) => bidHierarchyColumn({ projects, scopeSource: bidPackages.first!.scopeSource }),
    package: ({ projects, latestVersion, status, scopeSource }) =>
      latestVersion.version !== 1 || status.code !== BidPackageStatus.Draft
        ? bidHierarchyColumn({ projects, scopeSource })
        : emptyCell,
    mw: "120px",
  }),
  column<Row>({
    header: "Status",
    group: ({ status }) => (
      <div css={Css.df.aic.$}>
        <StatusIndicator status={status.code} type="BidPackage" />
        <span css={Css.ml1.$}>{status.name}</span>
      </div>
    ),
    package: ({ status }) => (
      <div css={Css.df.aic.$}>
        <StatusIndicator status={status.code} type="BidPackage" />
        <span css={Css.ml1.$}>{status.name}</span>
      </div>
    ),
    serverSideSortKey: "status",
    mw: "110px",
  }),
  column<Row>({
    header: "Source",
    group: emptyCell,
    package: ({ activeBidders }) => <BidPackageSource bidders={activeBidders} />,
    mw: "160px",
  }),
  dateColumn<Row>({
    header: "Last Edited",
    group: ({ updatedAt }) => dateCell(updatedAt),
    package: ({ updatedAt }) => dateCell(updatedAt),
    serverSideSortKey: "updatedAt",
    mw: "110px",
  }),
  actionColumn<Row>({
    header: emptyCell,
    group: emptyCell,
    package: (data) => ({
      content: data.canDelete.allowed && <IconButton inc={2} icon="trash" onClick={() => onDelete(data)} />,
    }),
    w: "60px",
    mw: "60px",
  }),
];

function createRows(data: BidPackageGroupsQuery | undefined): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...(data?.bidPackageGroups.entities ?? []).flatMap((bpg): GridDataRow<Row>[] => {
      const firstBidPackage = bpg.bidPackages.first!;
      // If we have published the first bid package don't show the group, this is because the groups are purely used for bulk bid package creation
      if (
        firstBidPackage.latestVersion.version !== 1 ||
        firstBidPackage.status.code !== BidPackageStatus.Draft ||
        // Ignore PBBv2 bid packages because they are not bulk created.
        bpg.isPlanBasedV2 ||
        // If we aren't creating more than one bid package, don't show the group
        bpg.bidPackages.length === 1
      ) {
        return bpg.bidPackages.map((bp): GridDataRow<Row> => ({ kind: "package" as const, id: bp.id, data: bp }));
      }
      return [
        {
          kind: "group" as const,
          id: bpg.id,
          data: bpg,
          children: bpg.bidPackages.map((bp) => ({ kind: "package" as const, id: bp.id, data: bp })),
        },
      ];
    }),
  ];
}

// Renders a source column given the bidders for a bid package
// Multiple bidders are stacked vertically.
// Show a tag for bidders kind=InternalEstimate with the estimate type.
export function BidPackageSource(props: { bidders: BidPackagesPage_BidPackageBidderFragment[] }) {
  const { bidders } = props;
  if (bidders.isEmpty) {
    return <span css={Css.gray700.$}>No bidders yet</span>;
  }

  const [estimates, trades] = bidders.partition((b) => b.kind === BidPackageBidderKind.InternalEstimate);

  const estimateTags = estimates.map((e) => {
    const estimateType = e.bidContract?.internalEstimateType?.code;
    const isProjectEstimate = estimateType === InternalEstimateType.Project;
    return (
      <div key={e.id}>
        <Tag
          key={e.id}
          // Unit-based internal estimates have no type, so keep the generic tag for them
          text={isProjectEstimate ? "PROJECT ESTIMATE" : estimateType ? "PROVISIONAL ESTIMATE" : "INTERNAL ESTIMATE"}
          type={isProjectEstimate ? "neutral" : "update"}
        />
      </div>
    );
  });

  const tradeNames = trades.map((t) => <span key={t.id}>{t.name}</span>);

  return (
    <div css={Css.df.fdc.gap1.$}>
      {estimateTags}
      {tradeNames}
    </div>
  );
}

/** A package can name 30+ cost codes, which stack a line at a time unless the caller opts into `truncate`. */
export function FormattedBidName({
  costCodesName,
  latestVersion,
  isPlanBasedV2,
  truncate,
}: {
  costCodesName: string;
  latestVersion: { displayVersion: string };
  isPlanBasedV2: boolean;
  truncate?: boolean;
}) {
  return (
    <div css={Css.df.fdc.if(!!truncate).mw0.$}>
      {truncate ? (
        <span css={Css.truncate.$}>{costCodesName}</span>
      ) : (
        <div css={Css.df.fdc.wsnw.$}>
          {costCodesName.split(",").map((n, i, arr) => (
            <span key={n}>
              {n}
              {i < arr.length - 1 ? "," : ""}
            </span>
          ))}
        </div>
      )}
      {!isPlanBasedV2 && <span css={Css.xs.$}>{latestVersion.displayVersion}</span>}
    </div>
  );
}

// Renders an "X/Y" badge; X: POFs on this bid's latest version, Y: POFs at the community/market scope.
export function ProductOfferingsBadge(props: Pick<BidPackageDetailFragment, "latestVersion" | "projects">) {
  const { latestVersion, projects } = props;
  const tid = useTestIds({}, "productOfferingsBadge");
  const onBid = latestVersion.readyPlanAggregateVersions.length;
  const total =
    projects
      .flatMap((p) => {
        if (p.__typename === "Community") return p.productOfferings;
        if (p.__typename === "Market") return p.children.flatMap((c) => c.productOfferings);
        return [];
      })
      .filter((p) => p.status.code === ReadyPlanStatus.Active).length || onBid;
  if (total === 0) return null;
  return (
    <Tooltip
      title={
        <div css={Css.df.fdc.gap1.$}>
          {latestVersion.readyPlanAggregateVersions.map((rpav) => (
            <div key={rpav.id}>
              {rpav.readyPlan.name} {rpav.displayVersion}
            </div>
          ))}
        </div>
      }
      placement="top"
    >
      <Tag {...tid} text={`${onBid === total ? "ALL " : ""}${onBid}/${total}`} type="neutral" />
    </Tooltip>
  );
}

// For now we don't want to list out all lots or have chips saying "All Lots in subdivision"
export function bidHierarchyColumn({
  projects,
  scopeSource,
}: {
  projects: NamedFragment[];
  scopeSource: Maybe<BidPackageDetailFragment["scopeSource"]>;
}) {
  return scopeSource?.code === BidScopeSource.Lot ? chipCell(["Lot"]) : chipCell(projects.map((p) => p.name));
}
